import React, { createContext, useContext, useMemo, type ReactNode } from "react";
import { ProvetApi, ProvetApiClient } from "../utilities";

/**
 * Static configuration context for Provet booking.
 * Contains data that never changes after initialization.
 * Components using only this context won't re-render when booking state changes.
 */
interface ProvetStaticContextType {
  api: ProvetApiClient;
  language: string;
  activeDepartmentIds: number[];
  excludedDepartmentIds: number[];
}

interface ProvetStaticProviderProps {
  children: ReactNode;
  language: string;
  antiForgeryToken?: string;
  activeDepartmentIds?: number[];
  excludedDepartmentIds?: number[];
}

const ProvetStaticContext = createContext<ProvetStaticContextType | undefined>(undefined);

export const ProvetStaticProvider = ({
  children,
  language,
  antiForgeryToken,
  activeDepartmentIds = [],
  excludedDepartmentIds = []
}: ProvetStaticProviderProps) => {
  // API instance is created once and never changes
  // Includes anti-forgery token for CSRF protection on POST requests
  const api = useMemo(
    () => new ProvetApi(undefined, antiForgeryToken),
    [antiForgeryToken]
  );

  // Memoize the entire context value since nothing ever changes
  const value = useMemo(
    () => ({
      api,
      language,
      activeDepartmentIds,
      excludedDepartmentIds
    }),
    [api, language, activeDepartmentIds, excludedDepartmentIds]
  );

  return (
    <ProvetStaticContext.Provider value={value}>{children}</ProvetStaticContext.Provider>
  );
};

export const useProvetStatic = () => {
  const context = useContext(ProvetStaticContext);
  if (context === undefined) {
    throw new Error("useProvetStatic must be used within a ProvetStaticProvider");
  }
  return context;
};

export type { ProvetStaticContextType };
